import React, { useState, Suspense } from 'react';
import { Box, Stack } from '@mui/material';
import axios, { endPoints } from 'api/request';
import { useMutation } from 'react-query';
import { catalogs, riCatalogs } from 'store';
import { riCatalogDialog, riSelectedCatalog } from 'pages/reference-implementation/store';
import { useRecoilState } from 'recoil';
import { getLoggedUserGPID, isRIEnabled } from 'utils';
import Empty from 'components/empty';
import landingStyle from 'pages/landing-new/style';
import { START_PROVISIONING_MESSAGE } from 'utils/constant';
import TabNavigation from './TabNavigation';
import RecommendationCard from './RecommendationCard';
import Recommendation from './style';
import AssistantSvg from '../images/assistant.svg';
import BatchPredictionSvg from '../images/batch_prediction.svg';
import BuildSvg from '../images/build.svg';

const RiCatalogService = React.lazy(() => import('pages/landing-new/components/RICatalogService'));
const CatalogService = React.lazy(() => import('pages/landing-new/components/CatalogService'));

const additionalNonRiRecommendations = [
    {
        catalogServiceCode: 'AZWINSQLIAAS',
        ordersCount: 1,
    },
    {
        catalogServiceCode: 'AZOELORA19C',
        ordersCount: 2,
    },
    {
        catalogServiceCode: 'AZWINVM2022',
        ordersCount: 6,
    },
];

const additionalRiRecommendations = [
    {
        catalogServiceCode: 'RI001A',
        ordersCount: 1,
    },
    {
        catalogServiceCode: 'RI003O',
        ordersCount: 2,
    },
    {
        catalogServiceCode: 'RIIOTEVENTHUB',
        ordersCount: 6,
    },
];

const generateRecommendations = (
    catalogRecommendationList,
    devXNonRiCatalogs,
    devXRiCatalogs,
    selectedTab,
    tabs,
    manualNonRiRecommendation,
    manualRiRecommendation
) => {
    let recommendations = catalogRecommendationList
        .map((recommendation) => {
            const matchedCatalog = devXNonRiCatalogs.find((catalog) => catalog.catalogDefinitionId === recommendation.catalogServiceCode);
            const matchedRiCatalog = devXRiCatalogs.find((riCatalog) => riCatalog['reference-implementation-id'] === recommendation.catalogServiceCode);

            if (matchedCatalog) {
                return {
                    catalogServiceCode: recommendation.catalogServiceCode,
                    iconSrc: matchedCatalog.iconPath,
                    title: `${matchedCatalog.displayName}`,
                    description: matchedCatalog.description,
                    tags: matchedCatalog.tags,
                    primaryButtonText: START_PROVISIONING_MESSAGE,
                    secondaryButtonText: 'Learn More',
                    cmpUri: matchedCatalog.cmpUri,
                    isRICatalog: false,
                    catalogSequence: matchedCatalog.catalogSequence,
                    data: matchedCatalog,
                };
            }
            if (matchedRiCatalog) {
                return {
                    catalogServiceCode: recommendation.catalogServiceCode,
                    iconSrc: matchedRiCatalog.iconPath,
                    title: matchedRiCatalog.name,
                    description: matchedRiCatalog.description,
                    tags: matchedRiCatalog.tags,
                    primaryButtonText: START_PROVISIONING_MESSAGE,
                    secondaryButtonText: 'Learn More',
                    cmpUri: matchedRiCatalog.provisioningPath,
                    isRICatalog: true,
                    catalogSequence: matchedRiCatalog['catalog-sequence'],
                    data: matchedRiCatalog,
                };
            }
            return null;
        })
        .filter(Boolean);

    // Add catalogs from manualRecommendation based on the length of recommendations
    if (catalogRecommendationList.length < 4) {
        const additionalCatalogsCount = Math.max(0, 4 - recommendations.length);
        const additionalNonRiCatalogs = manualNonRiRecommendation.slice(0, additionalCatalogsCount);
        const additionalRiCatalogs = manualRiRecommendation.slice(0, additionalCatalogsCount);
        const manualNonRiRecommendations = additionalNonRiCatalogs
            ?.map((recommendation) => {
                if (tabs[selectedTab]?.catalogType === 'ALL' || tabs[selectedTab]?.catalogType === 'SERVICES') {
                    const matchedCatalog = devXNonRiCatalogs.find((catalog) => catalog.catalogDefinitionId === recommendation.catalogServiceCode);
                    if (matchedCatalog) {
                        return {
                            catalogServiceCode: recommendation.catalogServiceCode,
                            iconSrc: matchedCatalog.iconPath,
                            title: `${matchedCatalog.displayName}`,
                            description: matchedCatalog.description,
                            tags: matchedCatalog.tags,
                            primaryButtonText: START_PROVISIONING_MESSAGE,
                            secondaryButtonText: 'Learn More',
                            cmpUri: matchedCatalog.cmpUri,
                            isRICatalog: false,
                            catalogSequence: matchedCatalog.catalogSequence,
                            data: matchedCatalog,
                        };
                    }
                }
                return null;
            })
            .filter(Boolean);
        const manualRiRecommendations = additionalRiCatalogs
            ?.map((recommendation) => {
                if (tabs[selectedTab]?.catalogType === 'SOLUTIONS') {
                    const matchedRiCatalog = devXRiCatalogs.find((riCatalog) => riCatalog['reference-implementation-id'] === recommendation.catalogServiceCode);
                    if (matchedRiCatalog) {
                        return {
                            catalogServiceCode: recommendation.catalogServiceCode,
                            iconSrc: matchedRiCatalog.iconPath,
                            title: matchedRiCatalog.name,
                            description: matchedRiCatalog.description,
                            tags: matchedRiCatalog.tags,
                            primaryButtonText: START_PROVISIONING_MESSAGE,
                            secondaryButtonText: 'Learn More',
                            cmpUri: matchedRiCatalog.provisioningPath,
                            isRICatalog: true,
                            catalogSequence: matchedRiCatalog['catalog-sequence'],
                            data: matchedRiCatalog,
                        };
                    }
                }
                return null;
            })
            .filter(Boolean);

        recommendations = [...recommendations, ...manualNonRiRecommendations, ...manualRiRecommendations];
    }
    return recommendations;
};

const RecommendationsSection = () => {
    const [devXNonRiCatalogs] = useRecoilState(catalogs);
    const [devXRiCatalogs] = useRecoilState(riCatalogs);
    const [selectedTab, setSelectedTab] = useState(0);
    const [, setIsOpen] = useRecoilState(riCatalogDialog);
    const [, setSelectedCatalog] = useRecoilState(riSelectedCatalog);

    const onClickLearnMoreBtn = (riData) => {
        setSelectedCatalog(riData);
        setIsOpen(true);
    };
    const recommendApi = useMutation((payload) => axios.post(endPoints.catalog.recommendation, payload));

    const tabs = [
        {
            iconSrc: AssistantSvg,
            text: 'Recommended for you',
            catalogType: 'ALL',
        },
        {
            iconSrc: BatchPredictionSvg,
            text: 'Solutions',
            catalogType: 'SOLUTIONS',
        },
        {
            iconSrc: BuildSvg,
            text: 'Services',
            catalogType: 'SERVICES',
        },
    ];

    const recommendations = generateRecommendations(
        recommendApi.data?.data?.catalogRecommendationList || [],
        devXNonRiCatalogs,
        devXRiCatalogs,
        selectedTab,
        tabs,
        additionalNonRiRecommendations,
        additionalRiRecommendations
    );

    const handleTabClick = (index) => {
        setSelectedTab(index);
    };

    const handleFetchRecommendations = () => {
        const payload = {
            catalogType: tabs[selectedTab]?.catalogType || 'ALL',
            requestorGpid: getLoggedUserGPID(),
            catalogCount: 10,
            daysToFetch: 180,
        };
        recommendApi.mutate(payload);
    };

    React.useEffect(() => {
        handleFetchRecommendations();
    }, [selectedTab?.toString()]);

    const renderRecommendations = () => {
        if (recommendApi?.isLoading) {
            return <Empty color="#000000" isLoading />;
        }

        if (recommendApi.data?.data?.catalogRecommendationList?.length) {
            return (
                <Box sx={{ ...Recommendation.Section.cardContainer, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                    {recommendations.map((recommendation, index) => (
                        <RecommendationCard
                            key={index}
                            onClickLearnMoreBtn={(riData) => onClickLearnMoreBtn(riData)}
                            {...recommendation}
                            forceToShowLearnMore
                            selectedTabCatalogType={tabs[selectedTab]?.catalogType}
                        />
                    ))}
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Suspense
                    fallback={
                        <Stack sx={landingStyle?.loadingStack}>
                            <Empty color="#000000" isLoading />
                        </Stack>
                    }
                >
                    <Box data-testid="landing-page-new" sx={landingStyle.landingWraper}>
                        {tabs[selectedTab]?.catalogType === 'ALL' && (
                            <>
                                {isRIEnabled() && <RiCatalogService />}
                                <CatalogService />
                            </>
                        )}
                        {tabs[selectedTab]?.catalogType === 'SOLUTIONS' && isRIEnabled() && <RiCatalogService />}
                        {tabs[selectedTab]?.catalogType === 'SERVICES' && <CatalogService />}
                    </Box>
                </Suspense>
            </Box>
        );
    };

    return (
        <Box sx={Recommendation.Section.container}>
            <TabNavigation tabs={tabs} selectedTab={selectedTab} onTabClick={handleTabClick} />
            {renderRecommendations()}
        </Box>
    );
};

export default RecommendationsSection;
